import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerListDocumentsQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
} from 'korint-sdk';
import type { StepperValidationRule } from '../components/layout/StepperLayout/useValidatedStepper';

export function useBrokerageFirmDocumentsValidationRule(): StepperValidationRule {
  const { brokerageFirmId } = useAssertedParams<{ brokerageFirmId: string }>();

  // Do NOT default to `[]` — an empty array would make `.every()` vacuously
  // true and silently bypass the gate while the tenant config is still loading
  // (or if it errors). Stay closed until we actually know what's required.
  const { data: requiredCategories } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    select: data =>
      (data.brokerageFirmDocuments?.brokerageFirmDocumentsList ?? [])
        .filter(d => d.required)
        .map(d => d.category),
  });

  const { data: uploadedCategories } = useQuery({
    ...brokerageFirmControllerListDocumentsQueryOptions(brokerageFirmId, {
      pageInfo: { count: 100 },
    }),
    select: data => {
      const cats = new Set<string>();
      for (const doc of data.data) {
        if (
          doc.type === 'supporting_document' &&
          doc.category &&
          doc.category !== 'uncategorized'
        ) {
          cats.add(doc.category);
        }
      }
      return cats;
    },
  });

  if (brokerageFirmId.includes('_test_')) {
    return { isValid: true };
  }

  return {
    isValid:
      !!requiredCategories &&
      !!uploadedCategories &&
      requiredCategories.every(c => uploadedCategories.has(c)),
  };
}
